import { create } from 'zustand';
import type { PackingTask, PackingItem, QualityCheck, QualityIssue, PackingState } from '../types';

interface PackingStore extends PackingState {
  // Actions
  loadPackingTasks: () => Promise<PackingTask[]>;
  startPackingTask: (taskId: string) => Promise<void>;
  updatePackingItem: (itemId: string, packed: boolean) => Promise<void>;
  addQualityCheck: (taskId: string, issues?: QualityIssue[]) => Promise<void>;
  completePackingTask: (taskId: string) => Promise<void>;
  generateShippingLabel: (taskId: string) => Promise<string>;
  setCurrentPackingTask: (task: PackingTask | null) => void;
  clearError: () => void;
}

export const usePackingStore = create<PackingStore>((set, get) => ({
  currentPackingTask: null,
  packingItems: [],
  qualityChecks: [],
  isLoading: false,
  error: null,

  loadPackingTasks: async () => {
    set({ isLoading: true, error: null });
    
    try {
      // For now, return empty array since we don't have packing tables yet
      // In a real implementation, this would query packing_tasks table
      const demoTasks: PackingTask[] = [];
      
      set({ isLoading: false });
      return demoTasks;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to load packing tasks',
        isLoading: false 
      });
      return [];
    }
  },

  startPackingTask: async (taskId: string) => {
    set({ isLoading: true, error: null });
    
    try {
      // For now, return null since we don't have packing tables yet
      // In a real implementation, this would query packing_tasks table
      const demoTask: PackingTask | null = null;
      
      set({ 
        currentPackingTask: demoTask,
        packingItems: [],
        isLoading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to start packing task',
        isLoading: false 
      });
    }
  },

  updatePackingItem: async (itemId: string, packed: boolean) => {
    set({ isLoading: true, error: null });
    
    try {
      const state = get();
      const updatedItems = state.packingItems.map(item =>
        item.id === itemId ? { ...item, packed } : item
      );
      
      set({ packingItems: updatedItems, isLoading: false });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to update packing item',
        isLoading: false 
      });
    }
  },

  addQualityCheck: async (taskId: string, issues?: QualityIssue[]) => {
    set({ isLoading: true, error: null });
    
    try {
      const newQualityCheck: QualityCheck = {
        id: `qc-${Date.now()}`,
        packingTaskId: taskId,
        checkedBy: 'current-user',
        status: issues && issues.length > 0 ? 'failed' : 'passed',
        issues,
        createdAt: new Date().toISOString()
      };
      
      const state = get();
      set({ 
        qualityChecks: [...state.qualityChecks, newQualityCheck],
        isLoading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to add quality check',
        isLoading: false 
      });
    }
  },

  completePackingTask: async (taskId: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const state = get();
      if (state.currentPackingTask) {
        const updatedTask = {
          ...state.currentPackingTask,
          status: 'completed' as const,
          updatedAt: new Date().toISOString()
        };
        
        set({ 
          currentPackingTask: updatedTask,
          isLoading: false 
        });
      }
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to complete packing task',
        isLoading: false 
      });
    }
  },

  generateShippingLabel: async (taskId: string) => {
    set({ isLoading: true, error: null });
    
    try {
      // Demo shipping label generation
      const labelUrl = `https://demo-labels.com/label-${taskId}.pdf`;
      
      const state = get();
      if (state.currentPackingTask) {
        const updatedTask = {
          ...state.currentPackingTask,
          shippingLabel: labelUrl,
          updatedAt: new Date().toISOString()
        };
        
        set({ 
          currentPackingTask: updatedTask,
          isLoading: false 
        });
      }
      
      return labelUrl;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to generate shipping label',
        isLoading: false 
      });
      throw error;
    }
  },

  setCurrentPackingTask: (task) => set({ currentPackingTask: task }),

  clearError: () => set({ error: null })
}));